import React, { FC, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Button as MUIButton,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Link,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { usePageStore } from "@/hooks/usePageStore";
import { useGetSettings } from "@/hooks/useSetting";
import { useGeofenceById } from "@/hooks/useGeofences";
import Breadcrumbs from "@/components/Breadcrumbs";
import Spinner from "@/components/Spinner";
import {
  GeoFenceAction,
  ValidationRequestSettings,
} from "@/domain/geofence.interface";

const geofenceSettingsFields: (keyof ValidationRequestSettings)[] = [
  "metersRadiusGeofenceRequestValidation",
  "metersGeofenceAccuracyThreshold",
  "minutesPingGeofenceValidationRequest",
  "minutesPingGeofenceAlive",
  "automaticGeofenceTrigger",
  "requestGeofenceValidationsPerDay",
  "minimumMinsGeofenceBetweenRequestValidations",
  "showGeofenceChip",
  "distanceFilterGeofenceRequestValidation",
  "minutesTimeToCompleteValidation",
  "validationRequestMaxAttempts",
];

const FieldRow: FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Box sx={{ display: "flex", py: 0.75 }}>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ minWidth: 240, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography variant="body2">{value ?? "-"}</Typography>
  </Box>
);

const GeofenceDetailPage: FC = () => {
  const { geofenceId } = useParams<{ geofenceId: string }>();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("geofence");
  const { data: settings } = useGetSettings();
  const navigate = useNavigate();

  const { data: geofence, isLoading } = useGeofenceById(geofenceId || "");

  const dateFormat =
    (settings?.generalDateFormat as string) || "DD MMM YYYY, hh:mm A";

  useEffect(() => {
    setPageTitle(t("detail.title"));
  }, []);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <Spinner />
      </Box>
    );
  }

  if (!geofence) {
    return (
      <Box>
        <Breadcrumbs
          items={[
            { label: t("pageTitle"), href: "/geofences", isLink: true },
            { label: t("detail.title"), isLink: false },
          ]}
        />
        <Typography sx={{ mt: 4 }}>Geofence not found.</Typography>
      </Box>
    );
  }

  const validationSettings =
    geofence.program?.configuration?.validationRequestSettings;

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t("pageTitle"), href: "/geofences", isLink: true },
          { label: t("detail.title"), isLink: false },
        ]}
      />

      <MUIButton
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mt: 1, mb: 1 }}
      >
        {t("detail.back")}
      </MUIButton>

      <Grid container spacing={3} sx={{ mt: 0 }}>
        {/* Event Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.eventInfo")}
              </Typography>
              <FieldRow
                label={t("detail.fields.action")}
                value={
                  <Chip
                    label={t(`action.${geofence.action}`)}
                    size="small"
                    color={
                      geofence.action === GeoFenceAction.ENTER
                        ? "success"
                        : "warning"
                    }
                    variant="outlined"
                  />
                }
              />
              <FieldRow
                label={t("detail.fields.timestamp")}
                value={dayjs(geofence.timestamp).format(dateFormat)}
              />
              <FieldRow
                label={t("detail.fields.latitude")}
                value={geofence.latitude}
              />
              <FieldRow
                label={t("detail.fields.longitude")}
                value={geofence.longitude}
              />
              <FieldRow
                label={t("detail.fields.accuracy")}
                value={
                  geofence.accuracy
                    ? t(`accuracy.${geofence.accuracy}`)
                    : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.addressType")}
                value={
                  geofence.addressType
                    ? t(`addressType.${geofence.addressType}`)
                    : "-"
                }
              />
              <FieldRow
                label={t("detail.fields.geofenceIdentifier")}
                value={geofence.geofenceIdentifier || "-"}
              />
              <FieldRow
                label={t("detail.fields.locationAccuracy")}
                value={geofence.locationAccuracy != null ? `${geofence.locationAccuracy} m` : "-"}
              />
              <FieldRow
                label={t("detail.fields.speed")}
                value={geofence.speed != null ? `${geofence.speed} m/s` : "-"}
              />
              <FieldRow
                label={t("detail.fields.batteryLevel")}
                value={geofence.batteryLevel != null ? `${geofence.batteryLevel}%` : "-"}
              />
              <FieldRow
                label={t("detail.fields.batteryCharging")}
                value={geofence.batteryCharging != null ? (geofence.batteryCharging ? "Yes" : "No") : "-"}
              />
              <FieldRow
                label={t("detail.fields.osType")}
                value={geofence.osType || "-"}
              />
              <FieldRow
                label={t("detail.fields.uuid")}
                value={geofence.uuid || "-"}
              />
              {geofence.error && (
                <FieldRow
                  label={t("detail.fields.error")}
                  value={
                    <Typography variant="body2" color="error">
                      {geofence.error}
                    </Typography>
                  }
                />
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Participant Information */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.participantInfo")}
              </Typography>
              {geofence.participant ? (
                <>
                  <FieldRow
                    label={t("detail.fields.participantName")}
                    value={
                      <Link
                        sx={{ cursor: "pointer" }}
                        onClick={() =>
                          navigate(
                            `/participants/${geofence.participant?._id}`,
                          )
                        }
                      >
                        {geofence.participant.firstName}{" "}
                        {geofence.participant.lastName}
                      </Link>
                    }
                  />
                  <FieldRow
                    label={t("detail.fields.email")}
                    value={geofence.participant.email || "-"}
                  />
                  <FieldRow
                    label={t("detail.fields.phoneNumber")}
                    value={geofence.participant.phoneNumber || "-"}
                  />
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  -
                </Typography>
              )}

              {geofence.enrollment && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <FieldRow
                    label={t("detail.fields.enrollmentStatus")}
                    value={
                      <Chip
                        label={geofence.enrollment.status}
                        size="small"
                        variant="outlined"
                      />
                    }
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Program & Geofence Configuration */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                {t("detail.programConfig")}
              </Typography>
              <FieldRow
                label={t("detail.fields.programName")}
                value={geofence.program?.name || "-"}
              />

              {validationSettings && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={0}>
                    {geofenceSettingsFields.map((field) => {
                      const value = validationSettings[field];
                      if (value === undefined || value === null) return null;

                      let displayValue: React.ReactNode;
                      if (typeof value === "boolean") {
                        displayValue = (
                          <Chip
                            label={value ? "Yes" : "No"}
                            size="small"
                            color={value ? "success" : "default"}
                            variant="outlined"
                          />
                        );
                      } else {
                        displayValue = String(value);
                      }

                      return (
                        <Grid item xs={12} md={6} key={field}>
                          <FieldRow
                            label={(t as (key: string) => string)(`detail.fields.${field}`)}
                            value={displayValue}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Related Records */}
        {(geofence.testResult || geofence.notification) && (
          <Grid item xs={12}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
                  {t("detail.relatedRecords")}
                </Typography>

                {geofence.testResult ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {t("detail.testResult")}
                    </Typography>
                    <FieldRow
                      label="Status"
                      value={geofence.testResult.status}
                    />
                    <FieldRow
                      label="Validation Type"
                      value={geofence.testResult.validationType}
                    />
                    <FieldRow
                      label="Created"
                      value={dayjs(geofence.testResult.createdAt).format(
                        dateFormat,
                      )}
                    />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t("detail.noTestResult")}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                {geofence.notification ? (
                  <Box sx={{ mt: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ fontWeight: 600, mb: 1 }}
                    >
                      {t("detail.notification")}
                    </Typography>
                    <FieldRow
                      label="Channel"
                      value={geofence.notification.channel}
                    />
                    <FieldRow
                      label="Status"
                      value={geofence.notification.status}
                    />
                    <FieldRow
                      label="Type"
                      value={geofence.notification.type}
                    />
                    {geofence.notification.sentAt && (
                      <FieldRow
                        label="Sent At"
                        value={dayjs(geofence.notification.sentAt).format(
                          dateFormat,
                        )}
                      />
                    )}
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    {t("detail.noNotification")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default GeofenceDetailPage;
